import React, { useRef, useState, useCallback } from 'react';
import { CircularProgress, Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { enqueueSnackbar } from 'notistack';

import CapturePhoto from 'modules/views/shared/CapturePhoto';
import Image from 'modules/views/shared/Image';
import { FieldWrapper, IField } from 'modules/views/pages/PersonalInformation/components/fieldWrapper';
import { ReactComponent as CameraIcon } from 'assets/icons/CameraIcon.svg';
import Services from 'services/Services';

import { IProfileInformationSchema } from '../schema';
import { useStyles } from '../styles';
import ProfilePictureMenu from './ProfilePictureMenu';
import UploadFromDevice, { IUploadFromDeviceRef } from './UploadFromDevice';
import ProfileGalleryModal, { IGalleryImage } from './ProfileGalleryModal';
import ProfileImagePreviewModal from './ProfileImagePreviewModal';

interface IPersonalInfoSectionProps {
  personalInformationFields: IField[];
  genderSectionFields: IField[];
  profilePhotoField: { isVisible: boolean; isRequired: boolean };
  values: IProfileInformationSchema;
  errors: Record<string, { message: string } | undefined>;
  setValue: (key: keyof IProfileInformationSchema, value: any) => void;
  isProfilePictureUploading: boolean;
  showCaptureModal: boolean;
  workerId: string;
  onOpenCaptureModal: () => void;
  onCloseCaptureModal: () => void;
  onUploadProfilePicture: (src: string | string[]) => void;
  onPiiFieldClick: () => void;
}

const PersonalInfoSection: React.FC<IPersonalInfoSectionProps> = ({
  personalInformationFields,
  genderSectionFields,
  profilePhotoField,
  values,
  errors,
  setValue,
  isProfilePictureUploading,
  showCaptureModal,
  workerId,
  onOpenCaptureModal,
  onCloseCaptureModal,
  onUploadProfilePicture,
  onPiiFieldClick,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const uploadFromDeviceRef = useRef<IUploadFromDeviceRef>(null);

  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [showGallery, setShowGallery] = useState(false);
  const [galleryImages, setGalleryImages] = useState<IGalleryImage[]>([]);
  const [totalGalleryImages, setTotalGalleryImages] = useState(0);
  const [isGalleryLoading, setIsGalleryLoading] = useState(false);
  const [previewImage, setPreviewImage] = useState<{ url: string; label: string } | null>(null);

  const profilePictureErrorMessage = errors.pictureUrl?.message || null;

  const handleProfilePicClick = (event: React.MouseEvent<HTMLDivElement>) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => setMenuAnchorEl(null);

  const handleCapturePhoto = () => {
    handleMenuClose();
    onOpenCaptureModal();
  };

  const handleUploadFromDevice = () => {
    handleMenuClose();
    uploadFromDeviceRef.current?.open();
  };

  const handleSeeGallery = useCallback(async () => {
    handleMenuClose();
    setShowGallery(true);
    setIsGalleryLoading(true);
    try {
      const res = await Services.getWorkerProfilePictures(workerId);
      setGalleryImages(res.data?.items || []);
      setTotalGalleryImages(res.data?.totalResults || 0);
    } catch {
      enqueueSnackbar(t('myProfile.profilePicture.gallery.loadError'), { variant: 'error' });
      setGalleryImages([]);
      setTotalGalleryImages(0);
    } finally {
      setIsGalleryLoading(false);
    }
  }, [workerId, t]);

  return (
    <>
      <div className={classes.profilePicMainContainer}>
        <div
          className={classes.profilePicContainer}
          onClick={isProfilePictureUploading ? undefined : handleProfilePicClick}
          role="button"
          data-testid="profile-picture-button"
          aria-label={t('personalInformation.profilePhoto')}
          style={isProfilePictureUploading ? { cursor: 'default' } : undefined}
        >
          {values.pictureUrl ? (
            <Image src={values.pictureUrl} alt="Profile" className={classes.profileImage} />
          ) : (
            <p className={classes.profilePicText}>
              {values.firstName ? values.firstName.charAt(0).toUpperCase() : 'D'}
            </p>
          )}
          {isProfilePictureUploading && (
            <div className={classes.profilePicUploadingOverlay}>
              <CircularProgress size={32} thickness={3} style={{ color: '#fff' }} />
            </div>
          )}
          <div className={classes.cameraIconContainer}>
            <CameraIcon />
          </div>
        </div>
        {profilePictureErrorMessage && (
          <p className={classes.profilePicErrorText} data-testid="profile-picture-error">
            {profilePictureErrorMessage}
          </p>
        )}
      </div>

      <ProfilePictureMenu
        anchorEl={menuAnchorEl}
        onClose={handleMenuClose}
        onCapturePhoto={handleCapturePhoto}
        onUploadFromDevice={handleUploadFromDevice}
        onSeeGallery={handleSeeGallery}
      />

      <UploadFromDevice ref={uploadFromDeviceRef} onUpload={src => onUploadProfilePicture(src)} />

      <p className={classes.sectionTitle} data-testid="profile-section-title-personal-information">
        {t('personalInformation.personalInformation.title')}
      </p>
      <Grid container spacing={3} data-testid="profile-section-personal-information">
        {personalInformationFields.map((field, index) => (
          <Grid item xs={12} md={6} lg={4} key={field.id}>
            <FieldWrapper
              field={field}
              index={index}
              value={values[field.id]}
              setValue={value => setValue(field.id as keyof IProfileInformationSchema, value)}
              error={errors[field.id]?.message}
              options={field.options}
              onPiiFieldClick={onPiiFieldClick}
            />
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3} className={classes.genderContainer} data-testid="profile-section-gender">
        {genderSectionFields.map((field, index) => (
          <Grid item xs={12} key={field.id}>
            <FieldWrapper
              field={field}
              index={index}
              value={values[field.id]}
              setValue={value => setValue(field.id as keyof IProfileInformationSchema, value)}
              error={errors[field.id]?.message}
              options={field.options}
              styleClass={classes.genderParent}
              onPiiFieldClick={onPiiFieldClick}
            />
          </Grid>
        ))}
      </Grid>

      {showCaptureModal && (
        <CapturePhoto
          isProceedLoading={isProfilePictureUploading}
          onClose={onCloseCaptureModal}
          onProceed={onUploadProfilePicture}
        />
      )}

      <ProfileGalleryModal
        open={showGallery}
        images={galleryImages}
        totalImages={totalGalleryImages}
        isLoading={isGalleryLoading}
        onClose={() => setShowGallery(false)}
        onImageExpand={(url, label) => setPreviewImage({ url, label })}
      />

      <ProfileImagePreviewModal imageUrl={previewImage?.url ?? null} title={previewImage?.label} onClose={() => setPreviewImage(null)} />
    </>
  );
};

export default PersonalInfoSection;
